import { useEffect, useRef, useState } from 'react';
import { benchApi } from '../services/api/benchApi';
import { BENCH_REPORT_FACETS, BENCH_REPORT_KEYS, BENCH_REPORT_BU_SCOPE } from '../constants/benchReportFacets';
import { pruneMissingSelectedPositions } from '../utils/benchPositionFacets';

/**
 * ADR-110 — Position options for the Bench Report: the current positions of individuals
 * on bench in the selected Cluster and Business Unit, grouped by org unit type
 * (Delivery first), with counts.
 *
 * Fetches on mount and when the Cluster or Business Unit changes — nothing else
 * (Bench Type, Star, Individual and Technologies do not narrow the options, and picking a
 * position does not refetch). The current selection is sent so selected positions stay
 * listed at 0; ids the server no longer knows are passed to `onSelectionChange` pruned.
 *
 * Latest-request-wins: a slow response for a previous scope is dropped.
 * `failed` tells the page to degrade to the selector's own catalog fetch.
 *
 * @param {Object} args
 * @param {string} args.clusterId - selected Cluster ('' for all)
 * @param {string} args.businessUnitId - selected Business Unit ('' for none)
 * @param {string[]} args.selectedPositionIds - current selection
 * @param {Function} [args.onSelectionChange] - receives the pruned selection
 * @returns {{ positionsData: Object[], facet: Object|null, isLoading: boolean, failed: boolean }}
 */
export const useBenchPositionFacets = ({ clusterId, businessUnitId, selectedPositionIds, onSelectionChange }) => {
  const [positionsData, setPositionsData] = useState([]);
  const [facet, setFacet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const selectedRef = useRef(selectedPositionIds);
  selectedRef.current = selectedPositionIds;
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const ticket = requestSeqRef.current + 1;
    requestSeqRef.current = ticket;
    const isCurrent = () => requestSeqRef.current === ticket;

    setIsLoading(true);

    benchApi.getBenchReportFacets({
      report: BENCH_REPORT_KEYS.BENCH,
      facets: [BENCH_REPORT_FACETS.POSITIONS],
      clusterId: clusterId || undefined,
      businessUnitId: businessUnitId || undefined,
      buScope: businessUnitId ? BENCH_REPORT_BU_SCOPE : undefined, // same scope the report is requested with
      selectedPositionIds: selectedRef.current,
    })
      .then((result) => {
        if (!isCurrent()) {
          return;
        }
        const positionsFacet = result?.[BENCH_REPORT_FACETS.POSITIONS] || null;
        setFacet(positionsFacet);
        setPositionsData(positionsFacet?.groups || []);
        setFailed(false);
        const pruned = pruneMissingSelectedPositions(positionsFacet, selectedRef.current);
        if (pruned && onSelectionChangeRef.current) {
          onSelectionChangeRef.current(pruned);
        }
      })
      .catch((error) => {
        if (!isCurrent()) {
          return;
        }
        // Degrade, don't fail (ADR-110 §6): the page falls back to the full position catalog.
        console.warn('Bench position options unavailable; falling back to the position catalog', error);
        setFailed(true);
      })
      .finally(() => {
        if (isCurrent()) {
          setIsLoading(false);
        }
      });
  }, [clusterId, businessUnitId]);

  return { positionsData, facet, isLoading, failed };
};

export default useBenchPositionFacets;
